import { inject, provide, ref } from 'vue';
import * as _ from 'lodash';
import { getBalances, getCashflowByMonth, getSyndesiCategories } from '../api/cashflowApi';
import { Balance, Cashflow, SyndesiCategories } from '../types/cashflow';

export interface MonthlyInOut {
  in: number;
  out: number;
  month: string;
  inBarHeight?: number;
  outBarHeight?: number;
  monthName?: string;
  netAmount?: number;
}

const INVESTABLE_INCOME_PERCENTAGE = 0.4;
const INVESTABLE_FUNDS_PERCENTAGE = 0.2;

const monthlyInOut = ref<MonthlyInOut[]>([]);
const monthlyCategoryAverages = ref<{categoryLabel: string; averageSpending: number}[]>([]);
const monthlyAvgDisposableIncome = ref<{ total: number; investable: number }>({ total: 0, investable: 0 });
const monthlyAvgIncomeSpending = ref<{ income: number; spending: number }>({ income: 0, spending: 0 });
const availableFunds = ref<{ total: number; investable: number }>({ total: 0, investable: 0 });
const totalInvestable = ref<number>(0);
const balances = ref<Balance[]>([]);
const isCashflowLoaded = ref<boolean>(false);
const syndesiCategories = ref<SyndesiCategories>({ insurance_and_both_expenses_per_month: 0, mortgage_expenses_per_month: 0 });
const isSyndesiCategoriesLoaded = ref<boolean>(false);
const validCashflowMonths = ref<number>(0);

const calculateMonthlyAverages = (cashflows: Cashflow[]): {
  avgDisposableIncome: { total: number; investable: number };
  avgIncomeSpending: { income: number; spending: number };
  validCashflows: number;
} => {
  const validCashflows = cashflows.filter(cashflow => cashflow.calculations);

  const validCount = validCashflows.length;
  if (validCount === 0) {
    return {
      avgDisposableIncome: { total: 0, investable: 0 },
      avgIncomeSpending: { income: 0, spending: 0 },
      validCashflows: 0,
    };
  }

  const totals = validCashflows.reduce(
    (acc, cashflow) => ({
      income: acc.income + (cashflow.calculations?.actualIncome || 0),
      spending: acc.spending + (cashflow.calculations?.actualSpending || 0),
    }),
    { income: 0, spending: 0 },
  );

  const avgIncome = Math.round(totals.income / validCount);
  const avgSpending = Math.round(totals.spending / validCount);
  const avgDisposableIncome = Math.round((totals.income - totals.spending) / validCount) || 0;

  return {
    avgDisposableIncome: {
      total: avgDisposableIncome,
      investable: Math.round(avgDisposableIncome * INVESTABLE_INCOME_PERCENTAGE),
    },
    avgIncomeSpending: {
      income: avgIncome,
      spending: avgSpending,
    },
    validCashflows: validCount,
  };
};

const calculateAvailableFunds = (balances: Balance[]): { total: number; investable: number } => {
  if (!balances || balances.length === 0) {
    return { total: 0, investable: 0 };
  }
  const totalFunds = balances.reduce((sum, account) => sum + account.balance, 0);
  return {
    total: Math.round(totalFunds),
    investable: Math.round(totalFunds * INVESTABLE_FUNDS_PERCENTAGE),
  };
};

interface CategoryAverage {
  categoryLabel: string;
  totalSpending: number;
  monthCount: number;
  averageSpending?: number;
}

const calculateMonthlyCategoryAverages = (cashflows: Cashflow[]): {categoryLabel: string; averageSpending: number}[] => {
  const result: CategoryAverage[] = [];

  cashflows.forEach(cashflow => {
    const categories = cashflow.categories || [];

    categories.forEach(category => {
      if (category.isIncome) { return; }

      const categoryLabel = category.label;
      const spending = category.monthlyCalculation?.total?.expected || 0;
      const existingCategory = result.find(item => item.categoryLabel === categoryLabel);

      if (existingCategory) {
        existingCategory.totalSpending += spending;
        existingCategory.monthCount += 1;
      } else {
        result.push({
          categoryLabel,
          totalSpending: spending,
          monthCount: 1,
        });
      }
    });
  });

  const categoriesWithAverage = result.map(category => ({
    categoryLabel: category.categoryLabel,
    averageSpending: category.totalSpending / category.monthCount,
  }));

  return categoriesWithAverage.sort((a, b) => b.averageSpending - a.averageSpending);
};

const extractMonthlyInOut = (cashflows: Cashflow[]): MonthlyInOut[] => {
  const last6Months = _.times(cashflows.length, i => {
    const date = new Date(new Date().setMonth(new Date().getMonth() - i));
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });

  const cashflowData: { [key: string]: MonthlyInOut } = {};
  cashflows.forEach(cashflow => {
    if (cashflow.cashflowDate) {
      cashflowData[cashflow.cashflowDate] = {
        in: cashflow.calculations?.actualIncome || 0,
        out: cashflow.calculations?.actualSpending || 0,
        month: cashflow.cashflowDate,
      };
    }
  });

  return last6Months.map(monthString =>
    cashflowData[monthString] || {
      in: 0,
      out: 0,
      month: monthString,
    }).reverse();
};

export async function getLastXMonthsCashflowsCashflow(months: number): Promise<Cashflow[]> {
  const now = new Date();
  const cashflows = await Promise.all(Array.from({ length: (months - 1) }, async (_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (i + 1));
    const cashflowMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return (await getCashflowByMonth(cashflowMonth));
  }));
  return cashflows;
}

const loadSyndesiCategories = async () => {
  syndesiCategories.value = await getSyndesiCategories();
  isSyndesiCategoriesLoaded.value = true;
  return {
    syndesiCategories: syndesiCategories.value,
    isSyndesiCategoriesLoaded: isSyndesiCategoriesLoaded.value,
  };
};

const loadCashflowData = async (numberOfMonths = 6) => {
  const cashflows = await getLastXMonthsCashflowsCashflow(numberOfMonths);

  monthlyCategoryAverages.value = calculateMonthlyCategoryAverages(cashflows);
  monthlyInOut.value = extractMonthlyInOut(cashflows);
  const { avgDisposableIncome, avgIncomeSpending, validCashflows } = calculateMonthlyAverages(cashflows);
  monthlyAvgDisposableIncome.value = avgDisposableIncome;
  monthlyAvgIncomeSpending.value = avgIncomeSpending;
  balances.value = await getBalances();
  validCashflowMonths.value = validCashflows;
  availableFunds.value = calculateAvailableFunds(balances.value);

  // Calculate total investable amount
  totalInvestable.value = availableFunds.value.investable + monthlyAvgDisposableIncome.value.investable;

  // Set cashflow loaded flag to true
  isCashflowLoaded.value = true;

  return {
    monthlyCategoryAverages: monthlyCategoryAverages.value,
    monthlyInOut: monthlyInOut.value,
    monthlyAvgDisposableIncome: monthlyAvgDisposableIncome.value,
    monthlyAvgIncomeSpending: monthlyAvgIncomeSpending.value,
    availableFunds: availableFunds.value,
    totalInvestable: totalInvestable.value,
    isCashflowLoaded: isCashflowLoaded.value,
    validCashflowMonths: validCashflowMonths.value,
  };
};

export const useCashflowService = () => {
  provide('monthlyCategoryAverages', monthlyCategoryAverages);
  provide('monthlyInOut', monthlyInOut);
  provide('monthlyAvgDisposableIncome', monthlyAvgDisposableIncome);
  provide('monthlyAvgIncomeSpending', monthlyAvgIncomeSpending);
  provide('availableFunds', availableFunds);
  provide('totalInvestable', totalInvestable);
  provide('loadCashflowData', loadCashflowData);
  provide('isCashflowLoaded', isCashflowLoaded);
  provide('loadSyndesiCategories', loadSyndesiCategories);
  provide('syndesiCategories', syndesiCategories);
  provide('isSyndesiCategoriesLoaded', isSyndesiCategoriesLoaded);
  provide('validCashflowMonths', validCashflowMonths);
};

export const useCashflow = () => ({
  monthlyCategoryAverages: inject('monthlyCategoryAverages'),
  monthlyInOut: inject('monthlyInOut'),
  availableFunds: inject('availableFunds'),
  totalInvestable: inject('totalInvestable'),
  loadCashflowData: inject('loadCashflowData'),
  isCashflowLoaded: inject('isCashflowLoaded'),
  monthlyAvgDisposableIncome: inject('monthlyAvgDisposableIncome'),
  monthlyAvgIncomeSpending: inject('monthlyAvgIncomeSpending'),
  loadSyndesiCategories: inject('loadSyndesiCategories'),
  syndesiCategories: inject('syndesiCategories'),
  isSyndesiCategoriesLoaded: inject('isSyndesiCategoriesLoaded'),
  validCashflowMonths: inject('validCashflowMonths'),
});
